<template>
  <a-modal
    :visible="value"
    title="Add events"
    width="600px"
    destroyOnClose
    @cancel="$emit('close')"
  >
    <div v-if="!events.length">No content</div>
    <div v-else class="content">
      <div class="top">
        <div class="breadcrumbs">
          <a-button class="level" type="link" @click="resetNavigation">
            Sports
          </a-button>
          <span v-for="(item, index) in breadcrumb" :key="index" class="crumb">
            <span class="divider">/</span>
            <a-button
              :class="currentTitle === item.name && 'level--selected'"
              class="level"
              type="link"
              @click="goToBreadcrumb(index)"
            >
              <span class="truncate" :title="item.name">
                {{ item.name }}
              </span>
            </a-button>
          </span>
        </div>

        <div v-if="selectedMatches?.length" class="selected">
          Selected: {{ selectedMatches.length }}
        </div>

        <div class="list-title">
          <a-button v-if="breadcrumb.length" type="link" @click="goBack">
            <a-icon type="arrow-left" />
          </a-button>
          <div class="truncate" :title="currentTitle">
            {{ currentTitle }}
          </div>
        </div>
      </div>

      <a-list v-if="isListView" :dataSource="currentList">
        <a-list-item
          class="item"
          slot="renderItem"
          slot-scope="item"
          @click="onItemClick(item)"
        >
          <div class="left">
            <span class="title truncate" :title="item.name">
              {{ item.name }}
            </span>
          </div>
          <div class="right">
            <div class="children">
              <span v-if="getSelectedChildren(item)">
                {{ getSelectedChildren(item) }} of
              </span>
              {{ item.count_matches }}
            </div>
            <a-icon
              v-if="loadingItemId === item.id"
              type="loading"
              style="font-size: 20px"
            />
            <img v-else src="/resources/images/chevron-right.svg" alt="right" />
          </div>
        </a-list-item>
      </a-list>

      <a-table
        v-else
        :rowKey="(record) => record.id"
        :columns="tableColumns"
        :dataSource="currentList"
        :pagination="false"
        :rowSelection="{
          selectedRowKeys: selectedIds,
          selectedRows: selectedMatches,
          onChange: onSelectionChange,
        }"
      />
    </div>

    <template slot="footer">
      <div class="footer">
        <div style="margin-left: auto">
          <a-button key="cancel" @click="$emit('close')"> Cancel </a-button>
          <a-button key="save" type="primary" @click="handleSubmit">
            Save
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>
<script>
export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      breadcrumb: [],
      selectedMatches: [],
      events: [],
      fullySelectedChamps: {},
      tableColumns: [
        { title: 'Name', dataIndex: 'name', key: 'name', width: '50%' },
        { title: 'ID', dataIndex: 'id', key: 'id', width: '20%' },
        {
          title: 'Activity',
          dataIndex: 'starts_at',
          key: 'activity',
          width: '20%',
          customRender: (text) =>
            text ? this.$moment(text).format('DD.MM.YYYY') : '-',
        },
      ],
      loadingItemId: null,
    }
  },
  computed: {
    selectedIds() {
      return this.selectedMatches.map((m) => m.id)
    },
    currentList() {
      if (!this.breadcrumb.length) {
        return this.events
      }

      let list = this.events

      this.breadcrumb.forEach((item) => {
        const found = list.find((i) => i.id === item.id)
        list = found && found.children ? found.children : []
      })

      return list
    },
    currentTitle() {
      return this.breadcrumb.length
        ? this.breadcrumb[this.breadcrumb.length - 1].name
        : 'Sports'
    },
    isListView() {
      return this.currentList.some((item) => item.children)
    },
  },
  methods: {
    async rebuildTreeFromSelections(selectedMatches) {
      this.selectedMatches = selectedMatches.map((m) => ({
        id: m.id,
        name: m.name,
        championshipId: m.championshipId,
        sportId: m.sportId,
        startsAt: m.startsAt,
      }))
    },
    getSelectedChildren(item) {
      if (!this.selectedMatches?.length || !item.children) return 0

      if (item.type === 'sport') {
        return this.selectedMatches?.filter((m) => m.sportId === item.id)
          ?.length
      }

      if (item.type === 'champ') {
        return this.selectedMatches?.filter((m) => m.championshipId === item.id)
          ?.length
      }
    },
    async onItemClick(item) {
      this.loadingItemId = item.id

      if (this.breadcrumb.length === 0) {
        const champs = await this.fetchChampionships(item.id)
        if (champs.length) {
          item.children = champs.map((c) => ({ ...c, children: [] }))
        }
      }

      if (this.breadcrumb.length === 1) {
        const matches = await this.fetchMatches(this.breadcrumb[0].id, item.id)

        item.children = matches
      }

      if (item.children) {
        this.breadcrumb.push(item)
      }

      this.loadingItemId = null
    },
    goToBreadcrumb(index) {
      this.breadcrumb.splice(index + 1)
    },
    goBack() {
      this.breadcrumb.pop()
    },
    resetNavigation() {
      this.breadcrumb = []
    },
    onSelectionChange(_, selectedRows) {
      const sportId = this.breadcrumb?.[0]?.id
      const championshipId = this.breadcrumb?.[1]?.id
      const championshipName = this.breadcrumb?.[1]?.name

      const outsideMatches = this.selectedMatches.filter(
        (m) => m.championshipId !== championshipId,
      )
      this.selectedMatches = [
        ...outsideMatches,
        ...selectedRows.map((match) => ({
          ...match,
          championshipId,
          championshipName,
          sportId,
        })),
      ]
    },
    handleCancel() {
      this.visible = false
    },
    handleSubmit() {
      const champs = new Map()

      this.selectedMatches.forEach((m) => {
        if (!champs.has(m.championshipId)) {
          champs.set(m.championshipId, {
            matches: [m],
            champ: {
              name: m.championshipName,
              id: m.championshipId,
              sportId: m.sportId,
            },
          })
        } else {
          const c = champs.get(m.championshipId)

          if (c) {
            c.matches.push(m)
          }
        }
      })

      const enrichedChamps = Object.fromEntries(champs.entries())

      const data = {
        champs: enrichedChamps,
      }

      this.$emit('submit', data)
    },
    async fetchSports() {
      return this.$store.dispatch('content/fetchSports')
    },
    async fetchChampionships(sportId) {
      return this.$store.dispatch('content/fetchChampionships', sportId)
    },
    async fetchMatches(sportId, champId) {
      return this.$store.dispatch('content/fetchMatches', { sportId, champId })
    },
  },
  async created() {
    const sports = await this.fetchSports()
    this.events = sports.map((sport) => ({ ...sport, children: [] }))

    if (this.selectedItems?.length) {
      await this.rebuildTreeFromSelections(this.selectedItems)
    }
  },
}
</script>
<style lang="scss" scoped>
@media (max-height: 900px) {
  ::v-deep .ant-modal {
    top: 20px !important;
    margin: 0 auto;
    transform: none !important;
    width: calc(100% - 32px);
    max-width: 100%;
    height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;

    .ant-modal-content {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .ant-modal-body {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow-y: auto;
      max-height: calc(100vh - 110px);
    }
  }

  .content {
    max-height: 100% !important;
  }
}

.top {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 10;
  padding-bottom: 16px;
}

.content {
  position: relative;
  max-height: 600px;
  overflow-y: auto;
}

.truncate {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  .crumb {
    display: flex;
    align-items: center;
  }

  .level {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    height: auto;

    span {
      max-width: 250px;
      pointer-events: all;
    }
  }

  .level--selected {
    color: #8c8c8c;
    cursor: default;
  }

  .divider {
    pointer-events: none;
    margin-right: 10px;
    color: #8c8c8c;
  }
}

.selected {
  position: absolute;
  right: 20px;
  top: 0;
  font-size: 14px;
  line-height: 22px;
  color: #8c8c8c;
}

.list-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #000;
  height: 24px;

  ::v-deep .ant-btn {
    padding: 0;
    border: none;
    height: auto;
    color: #000;
  }
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 36px;
  padding: 16px;
  border: 1px solid #e8e8e8 !important;
  border-left: none !important;
  border-right: none !important;
  cursor: pointer;
  font-size: 14px;
  line-height: 22px;

  & + & {
    margin-top: -1px;
  }

  .left {
    display: flex;
    align-items: center;
    flex: 0 1 auto;
    width: 100%;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
  }

  .title {
    color: #262626;
    max-width: 400px;
  }

  .children {
    color: #8c8c8c;
  }
}
</style>
